// diff-view.js - Diff View for Editor Tools
// Editor shows original with red removed markers, panel shows new with green added markers
//
// Der Diff oeffnet sich NICHT von selbst. Nach einem Tool-Lauf wird er nur
// berechnet; sichtbar wird er allein ueber den Diff-Knopf in der Werkzeugleiste,
// der die Zaehler (-entfernt / +hinzugefuegt) traegt. Sobald im Editor
// weitergearbeitet wird, ist der Diff hinfaellig und der Knopf verschwindet.

export class DiffView {
  constructor() {
    this.originalText = '';
    this.modifiedText = '';
    this.isVisible = false;
    this.panel = null;
    this.editorOverlay = null;
    this.initialized = false;
    this.diff = null;
    // Der Diff wird erst beim Oeffnen ins DOM geschrieben.
    this.rendered = false;
  }

  init() {
    this.panel = document.getElementById('diff-view-panel');
    this.editorOverlay = document.getElementById('diff-editor-overlay');

    if (!this.panel) {
      console.warn('DiffView: Panel element not found');
      return;
    }

    this.setupEventListeners();
    this.initialized = true;
  }

  setupEventListeners() {
    // Close buttons (legacy header button + floating centered button)
    const closeBtn = document.getElementById('diff-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.hide());
    }
    const closeFloating = document.getElementById('diff-close-floating');
    if (closeFloating) {
      closeFloating.addEventListener('click', () => this.hide());
    }

    // Toggle button
    const toggleBtn = document.getElementById('diff-toggle-btn');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => this.toggle());
    }

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible) {
        this.hide();
      }
    });

    // Update width on window resize
    window.addEventListener('resize', () => {
      if (this.isVisible) {
        this.syncPanelWidth();
      }
    });

  }

  /**
   * Jede Editor-Aenderung verwirft den Diff — Schreiben wie Loeschen. Danach
   * passt die Gegenueberstellung nicht mehr zum Text, also verschwindet auch
   * der Knopf wieder.
   * Bei offenem Diff ist der echte Editor ausgeblendet, von dort kommt nichts.
   */
  onEditorChange() {
    if (!this.isVisible && this.hasDiff()) {
      this.clear();
    }
  }

  /**
   * Sync diff panel width with editor panel width
   */
  syncPanelWidth() {
    const editorPanel = document.getElementById('markdown-editor');
    if (editorPanel && this.panel) {
      const editorWidth = editorPanel.offsetWidth;
      this.panel.style.width = `${editorWidth}px`;
    }
  }

  /**
   * Capture original text before tool execution
   * @param {string} text - The original editor content
   */
  captureOriginal(text) {
    // Erst den alten Diff abraeumen. Sonst raeumt ihn das erste setText des
    // Laufs ueber onEditorChange ab und nimmt dabei die gerade gesetzte
    // Grundlage mit — der Diff zeigte danach das ganze Dokument als neu.
    this.clear();
    this.originalText = text;
  }

  /**
   * Diff berechnen und den Knopf samt Zaehlern auffrischen — ohne ihn zu
   * oeffnen. Gerendert wird erst in show(), damit das Tippen nicht bei jeder
   * Neuberechnung Hunderte Zeilen ins DOM schreibt.
   * @param {string} modifiedText - Der aktuelle Editor-Inhalt
   */
  prepareDiff(modifiedText) {
    if (!this.initialized) {
      this.init();
    }

    if (!this.panel) {
      console.warn('DiffView: Cannot prepare diff, panel not initialized');
      return;
    }

    this.modifiedText = modifiedText;
    this.rendered = false;

    if (this.originalText === modifiedText) {
      this.diff = null;
      this.updateToggleButton();
      return;
    }

    this.diff = this.computeLineDiff(this.originalText, modifiedText);
    this.updateToggleButton();
  }

  /**
   * Compute line-based diff using LCS algorithm, with intra-line word diff
   * for paired modified lines so small edits don't mark the full line.
   */
  computeLineDiff(original, modified) {
    const origLines = original.split('\n');
    const modLines = modified.split('\n');
    const lcs = this.computeLCS(origLines, modLines);

    const originalDiff = [];
    const modifiedDiff = [];

    let origIdx = 0;
    let modIdx = 0;
    let lcsIdx = 0;

    while (origIdx < origLines.length || modIdx < modLines.length) {
      const origMatchesLCS = lcsIdx < lcs.length && origIdx < origLines.length && origLines[origIdx] === lcs[lcsIdx];
      const modMatchesLCS = lcsIdx < lcs.length && modIdx < modLines.length && modLines[modIdx] === lcs[lcsIdx];

      if (origMatchesLCS && modMatchesLCS) {
        originalDiff.push({ type: 'unchanged', text: origLines[origIdx] });
        modifiedDiff.push({ type: 'unchanged', text: modLines[modIdx] });
        origIdx++; modIdx++; lcsIdx++;
        continue;
      }

      // Collect chunks of non-LCS lines on both sides up to next anchor
      const origChunk = [];
      while (
        origIdx < origLines.length &&
        !(lcsIdx < lcs.length && origLines[origIdx] === lcs[lcsIdx])
      ) {
        origChunk.push(origLines[origIdx++]);
      }
      const modChunk = [];
      while (
        modIdx < modLines.length &&
        !(lcsIdx < lcs.length && modLines[modIdx] === lcs[lcsIdx])
      ) {
        modChunk.push(modLines[modIdx++]);
      }

      this.alignChunk(origChunk, modChunk, originalDiff, modifiedDiff);
    }

    const stats = {
      added: modifiedDiff.filter(l => l.type === 'added' || l.type === 'modified').length,
      removed: originalDiff.filter(l => l.type === 'removed' || l.type === 'modified').length
    };

    return { original: originalDiff, modified: modifiedDiff, stats };
  }

  /**
   * Deprecated: cross-line move suppression produced false negatives when
   * identical tokens were independently added and removed on different
   * lines. Kept as a stub so existing references don't break; callers no
   * longer invoke it.
   */
  suppressMovedTokens(originalDiff, modifiedDiff) {
    const isWs = (t) => /^\s+$/.test(t);

    // Record every non-whitespace `changed` token occurrence along with the
    // row index it sits on in its side of the chunk.
    const collect = (diff) => {
      const occ = new Map();
      diff.forEach((line, lineIdx) => {
        if (!line.segments) return;
        for (const seg of line.segments) {
          if (seg.type !== 'changed') continue;
          for (const p of seg.text.split(/(\s+)/)) {
            if (!p || isWs(p)) continue;
            if (!occ.has(p)) occ.set(p, []);
            occ.get(p).push(lineIdx);
          }
        }
      });
      return occ;
    };

    const origOcc = collect(originalDiff);
    const modOcc = collect(modifiedDiff);
    if (origOcc.size === 0 || modOcc.size === 0) return;

    // For each shared token, greedily pair orig occurrences with mod
    // occurrences that live on DIFFERENT rows. Same-row changes (e.g. an
    // in-line reorder) are left highlighted because they represent a real
    // local edit, not a cross-line move.
    const origAllowed = new Map();
    const modAllowed = new Map();
    for (const [tok, oLines] of origOcc) {
      const mLines = modOcc.get(tok);
      if (!mLines) continue;
      const oRem = [...oLines];
      const mRem = [...mLines];
      let oBudget = 0;
      let mBudget = 0;
      for (let i = 0; i < oRem.length; i++) {
        const jIdx = mRem.findIndex(l => l !== oRem[i]);
        if (jIdx === -1) continue;
        mRem.splice(jIdx, 1);
        oBudget++;
        mBudget++;
      }
      if (oBudget > 0) origAllowed.set(tok, oBudget);
      if (mBudget > 0) modAllowed.set(tok, mBudget);
    }
    if (origAllowed.size === 0 && modAllowed.size === 0) return;

    const demote = (diff, budgetMap) => {
      const budget = new Map(budgetMap);
      for (const line of diff) {
        if (!line.segments) continue;
        const out = [];
        for (const seg of line.segments) {
          if (seg.type !== 'changed') { out.push(seg); continue; }
          for (const p of seg.text.split(/(\s+)/)) {
            if (!p) continue;
            if (!isWs(p) && (budget.get(p) || 0) > 0) {
              budget.set(p, budget.get(p) - 1);
              out.push({ type: 'same', text: p });
            } else {
              out.push({ type: seg.type, text: p });
            }
          }
        }
        line.segments = this.coalesceSegments(out);
      }
    };

    demote(originalDiff, origAllowed);
    demote(modifiedDiff, modAllowed);
  }

  /**
   * Merge adjacent segments of the same type so rendering emits minimal
   * span/text nodes and no visual break across them.
   */
  coalesceSegments(segments) {
    const out = [];
    for (const seg of segments) {
      const last = out[out.length - 1];
      if (last && last.type === seg.type) last.text += seg.text;
      else out.push({ type: seg.type, text: seg.text });
    }
    return out;
  }

  /**
   * Align a chunk of non-matching lines by index. For sufficiently similar
   * pairs, emit a `modified` row with word-level segments so the rendered
   * diff highlights only the changed tokens. Dissimilar pairs fall back to
   * full removed+added lines.
   */
  alignChunk(origChunk, modChunk, origOut, modOut) {
    const m = origChunk.length;
    const n = modChunk.length;
    const SIM_THRESHOLD = 0.3;
    // Two-tier cap: MAX_PAIR_CELLS gates the expensive similarity DP and
    // drops to a cheap line-LCS realignment. HARD_BAILOUT_CELLS gates the
    // cheap LCS too, dropping to pure positional pairing with no LCS and
    // no word diff. The hard bailout is a guard against pathological huge
    // rewrites so the diff never locks the main thread.
    const MAX_PAIR_CELLS = 2000;
    const HARD_BAILOUT_CELLS = 100000;

    if (m === 0) {
      for (const line of modChunk) {
        origOut.push({ type: 'placeholder', text: '' });
        modOut.push({ type: 'added', text: line });
      }
      return;
    }
    if (n === 0) {
      for (const line of origChunk) {
        origOut.push({ type: 'removed', text: line });
        modOut.push({ type: 'placeholder', text: '' });
      }
      return;
    }

    if (m * n > HARD_BAILOUT_CELLS) {
      // Pathological chunk size. Emit pure remove+add without any LCS or
      // word-diff work so diff rendering can never freeze the UI.
      const pairs = Math.min(m, n);
      for (let k = 0; k < pairs; k++) {
        origOut.push({ type: 'removed', text: origChunk[k] });
        modOut.push({ type: 'added', text: modChunk[k] });
      }
      for (let k = pairs; k < m; k++) {
        origOut.push({ type: 'removed', text: origChunk[k] });
        modOut.push({ type: 'placeholder', text: '' });
      }
      for (let k = pairs; k < n; k++) {
        origOut.push({ type: 'placeholder', text: '' });
        modOut.push({ type: 'added', text: modChunk[k] });
      }
      return;
    }

    if (m * n > MAX_PAIR_CELLS) {
      // Fast path for big chunks. Skip the O(m*n) similarity DP, but
      // first run a cheap line-level LCS on whitespace-normalized content
      // so shifted-but-matching lines realign correctly. Non-matching
      // stretches then fall through to positional pairing with per-pair
      // word-level diff. Cost bounded by HARD_BAILOUT_CELLS above.
      const norm = (s) => s.replace(/\s+/g, ' ').trim();
      const origNorm = origChunk.map(norm);
      const modNorm = modChunk.map(norm);
      const lcs = this.computeLCS(origNorm, modNorm);

      const emitPair = (o, mLine) => {
        if (o === mLine) {
          origOut.push({ type: 'unchanged', text: o });
          modOut.push({ type: 'unchanged', text: mLine });
          return;
        }
        const wd = this.computeWordDiff(o, mLine);
        if (wd.similarity >= SIM_THRESHOLD) {
          origOut.push({ type: 'modified', text: o, segments: wd.orig });
          modOut.push({ type: 'modified', text: mLine, segments: wd.mod });
        } else {
          origOut.push({ type: 'removed', text: o });
          modOut.push({ type: 'added', text: mLine });
        }
      };

      let oi = 0, mi = 0, li = 0;
      while (oi < m || mi < n) {
        const oMatch = li < lcs.length && oi < m && origNorm[oi] === lcs[li];
        const mMatch = li < lcs.length && mi < n && modNorm[mi] === lcs[li];
        if (oMatch && mMatch) {
          // Normalized equal — emit as paired (word diff picks up any
          // whitespace-only differences between the raw lines).
          emitPair(origChunk[oi], modChunk[mi]);
          oi++; mi++; li++;
          continue;
        }
        const oSub = [];
        while (oi < m && !(li < lcs.length && origNorm[oi] === lcs[li])) {
          oSub.push(origChunk[oi++]);
        }
        const mSub = [];
        while (mi < n && !(li < lcs.length && modNorm[mi] === lcs[li])) {
          mSub.push(modChunk[mi++]);
        }
        const pairs = Math.min(oSub.length, mSub.length);
        for (let k = 0; k < pairs; k++) emitPair(oSub[k], mSub[k]);
        for (let k = pairs; k < oSub.length; k++) {
          origOut.push({ type: 'removed', text: oSub[k] });
          modOut.push({ type: 'placeholder', text: '' });
        }
        for (let k = pairs; k < mSub.length; k++) {
          origOut.push({ type: 'placeholder', text: '' });
          modOut.push({ type: 'added', text: mSub[k] });
        }
      }
      return;
    }

    // Adaptive band: small chunks run unbanded so a single edited line that
    // sits after a handful of inserts still pairs correctly. Bigger chunks
    // get a wider band scaled with chunk size to bound work.
    const maxDim = Math.max(m, n);
    const BAND = maxDim <= 12
      ? maxDim
      : Math.max(6, Math.ceil(maxDim / 4));

    // Cache word-diff results to avoid recomputing during backtracking
    const cache = Array.from({ length: m }, () => new Array(n).fill(null));
    const inBand = (i, j) => Math.abs(i - j) <= BAND;
    const diffAt = (i, j) => {
      if (!inBand(i, j)) return null;
      if (!cache[i][j]) cache[i][j] = this.computeWordDiff(origChunk[i], modChunk[j]);
      return cache[i][j];
    };

    // Order-preserving DP, pairing only within the band
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        let best = Math.max(dp[i - 1][j], dp[i][j - 1]);
        const wd = diffAt(i - 1, j - 1);
        if (wd && wd.similarity >= SIM_THRESHOLD) {
          const pair = dp[i - 1][j - 1] + wd.similarity;
          if (pair > best) best = pair;
        }
        dp[i][j] = best;
      }
    }

    // Backtrack. When pairing isn't eligible but dp is flat (no score was
    // gained on this cell via any direction), we must choose carefully so
    // reverse() produces index-ordered output:
    //   - If both sides still have lines AND the diagonal is on the same
    //     dp level, emit `replace` so unrelated pairs stay aligned row-wise.
    //   - Otherwise prefer the side that has more remaining lines first, so
    //     trailing pure adds/removes end up at the end after reverse.
    // Floating-point similarity scores accumulate rounding drift, so
    // direct equality on DP values can misroute the backtrack. Compare
    // with an epsilon tolerance.
    const EPS = 1e-9;
    const eq = (a, b) => Math.abs(a - b) < EPS;

    const ops = [];
    let i = m, j = n;
    while (i > 0 && j > 0) {
      const wd = diffAt(i - 1, j - 1);
      if (wd && wd.similarity >= SIM_THRESHOLD && eq(dp[i][j], dp[i - 1][j - 1] + wd.similarity)) {
        ops.push({ type: 'pair', i: i - 1, j: j - 1 });
        i--; j--;
      } else if (eq(dp[i][j], dp[i - 1][j - 1])) {
        if (i > j) {
          ops.push({ type: 'remove', i: i - 1 });
          i--;
        } else if (j > i) {
          ops.push({ type: 'add', j: j - 1 });
          j--;
        } else {
          ops.push({ type: 'replace', i: i - 1, j: j - 1 });
          i--; j--;
        }
      } else if (eq(dp[i][j], dp[i - 1][j])) {
        ops.push({ type: 'remove', i: i - 1 });
        i--;
      } else {
        ops.push({ type: 'add', j: j - 1 });
        j--;
      }
    }
    while (i > 0) { i--; ops.push({ type: 'remove', i }); }
    while (j > 0) { j--; ops.push({ type: 'add', j }); }
    ops.reverse();

    for (const op of ops) {
      if (op.type === 'pair') {
        const o = origChunk[op.i];
        const mLine = modChunk[op.j];
        if (o === mLine) {
          origOut.push({ type: 'unchanged', text: o });
          modOut.push({ type: 'unchanged', text: mLine });
        } else {
          const wd = cache[op.i][op.j];
          origOut.push({ type: 'modified', text: o, segments: wd.orig });
          modOut.push({ type: 'modified', text: mLine, segments: wd.mod });
        }
      } else if (op.type === 'replace') {
        origOut.push({ type: 'removed', text: origChunk[op.i] });
        modOut.push({ type: 'added', text: modChunk[op.j] });
      } else if (op.type === 'remove') {
        origOut.push({ type: 'removed', text: origChunk[op.i] });
        modOut.push({ type: 'placeholder', text: '' });
      } else {
        origOut.push({ type: 'placeholder', text: '' });
        modOut.push({ type: 'added', text: modChunk[op.j] });
      }
    }
  }

  /**
   * Compute word-level diff between two lines. Whitespace is kept as its own
   * token so grouping stays natural. Returns aligned segment arrays plus a
   * similarity score in [0, 1] based on token LCS coverage.
   */
  computeWordDiff(a, b) {
    const tokA = a.split(/(\s+)/).filter(t => t.length > 0);
    const tokB = b.split(/(\s+)/).filter(t => t.length > 0);

    if (tokA.length === 0 && tokB.length === 0) {
      return { orig: [], mod: [], similarity: 1 };
    }

    // Token-level LCS is O(tokens^2). For extreme lines (minified JSON,
    // pasted base64, etc.) that would freeze the main thread — bail out
    // with a whole-line replacement. Similarity is heuristically derived
    // from length ratio so alignChunk can still decide pair vs replace.
    const MAX_TOKEN_CELLS = 20000;
    if (tokA.length * tokB.length > MAX_TOKEN_CELLS) {
      const origSegFlat = tokA.length ? [{ type: 'changed', text: a }] : [];
      const modSegFlat = tokB.length ? [{ type: 'changed', text: b }] : [];
      const lo = Math.min(tokA.length, tokB.length);
      const hi = Math.max(tokA.length, tokB.length);
      const similarity = hi > 0 ? lo / hi * 0.5 : 0; // cap below threshold bias
      return { orig: origSegFlat, mod: modSegFlat, similarity };
    }

    const tokLcs = this.computeLCS(tokA, tokB);
    const origSeg = [];
    const modSeg = [];

    const push = (arr, type, text) => {
      if (!text) return;
      const last = arr[arr.length - 1];
      if (last && last.type === type) last.text += text;
      else arr.push({ type, text });
    };

    let i = 0, j = 0, k = 0;
    while (i < tokA.length || j < tokB.length) {
      const aMatch = k < tokLcs.length && i < tokA.length && tokA[i] === tokLcs[k];
      const bMatch = k < tokLcs.length && j < tokB.length && tokB[j] === tokLcs[k];

      if (aMatch && bMatch) {
        push(origSeg, 'same', tokA[i]);
        push(modSeg, 'same', tokB[j]);
        i++; j++; k++;
      } else if (aMatch) {
        push(modSeg, 'changed', tokB[j++]);
      } else if (bMatch) {
        push(origSeg, 'changed', tokA[i++]);
      } else if (i < tokA.length && j < tokB.length) {
        push(origSeg, 'changed', tokA[i++]);
        push(modSeg, 'changed', tokB[j++]);
      } else if (i < tokA.length) {
        push(origSeg, 'changed', tokA[i++]);
      } else if (j < tokB.length) {
        push(modSeg, 'changed', tokB[j++]);
      }
    }

    // Similarity is based on non-whitespace tokens only so two unrelated
    // lines with the same indentation/spacing don't cross the pairing
    // threshold just because their whitespace matches.
    const isWs = (t) => /^\s+$/.test(t);
    const sigA = tokA.filter(t => !isWs(t));
    const sigB = tokB.filter(t => !isWs(t));
    const sigLcs = (sigA.length && sigB.length) ? this.computeLCS(sigA, sigB) : [];
    const sigMax = Math.max(sigA.length, sigB.length);
    const similarity = sigMax > 0 ? sigLcs.length / sigMax : (tokA.length === tokB.length ? 1 : 0);
    return { orig: origSeg, mod: modSeg, similarity };
  }

  /**
   * Compute Longest Common Subsequence of two arrays
   */
  computeLCS(a, b) {
    const m = a.length;
    const n = b.length;

    // Build LCS length table
    const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (a[i - 1] === b[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
      }
    }

    // Backtrack to find LCS
    const lcs = [];
    let i = m, j = n;

    while (i > 0 && j > 0) {
      if (a[i - 1] === b[j - 1]) {
        lcs.unshift(a[i - 1]);
        i--;
        j--;
      } else if (dp[i - 1][j] > dp[i][j - 1]) {
        i--;
      } else {
        j--;
      }
    }

    return lcs;
  }

  /**
   * Render the diff - original in editor overlay, modified in panel
   */
  renderDiff(diff) {
    // Render original (with removed lines marked) in editor overlay
    this.renderEditorOverlay(diff.original);

    // Render modified (with added lines marked) in panel
    this.renderModifiedPanel(diff.modified);

    // Update stats - split between editor (removed) and panel (added)
    const statsEditorContainer = document.getElementById('diff-stats-editor');
    const statsPanelContainer = document.getElementById('diff-stats');

    if (statsEditorContainer) {
      statsEditorContainer.innerHTML = `
        <span class="diff-stat diff-stat-removed">
          <span class="diff-stat-icon">-</span>${diff.stats.removed} entfernt
        </span>
      `;
    }

    if (statsPanelContainer) {
      statsPanelContainer.innerHTML = `
        <span class="diff-stat diff-stat-added">
          <span class="diff-stat-icon">+</span>${diff.stats.added} hinzugefügt
        </span>
      `;
    }

    // Setup scroll sync between editor overlay and diff panel
    this.setupScrollSync();
  }

  /**
   * Render original text with red removed markers in editor overlay
   */
  renderEditorOverlay(originalDiff) {
    if (!this.editorOverlay) return;

    this.editorOverlay.innerHTML = '';
    originalDiff.forEach(line => {
      this.editorOverlay.appendChild(this.buildLineElement(line, 'removed'));
    });
  }

  /**
   * Render modified text with green added markers in panel
   */
  renderModifiedPanel(modifiedDiff) {
    const modifiedContainer = document.getElementById('diff-modified-lines');
    if (!modifiedContainer) return;

    modifiedContainer.innerHTML = '';
    modifiedDiff.forEach(line => {
      modifiedContainer.appendChild(this.buildLineElement(line, 'added'));
    });
  }

  /**
   * Build a line element. For `modified` rows, emit inline spans so only the
   * changed tokens get strong highlighting while context stays readable.
   */
  buildLineElement(line, side) {
    const el = document.createElement('div');
    if (line.type === 'modified') {
      const segments = line.segments || [];
      const hasVisibleChange = segments.some(s => s.type === 'changed' && !/^\s+$/.test(s.text));
      const hasAnyChange = segments.some(s => s.type === 'changed');
      // If the only remaining changes are whitespace (e.g. trailing-space or
      // indentation-only edits), fall back to the full-line highlight so the
      // change doesn't render invisible.
      if (!hasVisibleChange && hasAnyChange) {
        el.className = `diff-line diff-line-${side === 'removed' ? 'removed' : 'added'}`;
        el.textContent = line.text || '\u00A0';
        return el;
      }
      // If there are no changes left (e.g. all tokens were demoted as moved),
      // render as unchanged context.
      if (!hasAnyChange) {
        el.className = 'diff-line diff-line-unchanged';
        el.textContent = line.text || '\u00A0';
        return el;
      }
      el.className = `diff-line diff-line-modified-${side}`;
      if (segments.length === 0) {
        el.textContent = '\u00A0';
        return el;
      }
      segments.forEach(seg => {
        if (seg.type === 'changed') {
          // Whitespace-only changes must not get padded highlight boxes:
          // the extra width would break the paired-line scrollSync which
          // assumes matching line heights on both panes.
          if (/^\s+$/.test(seg.text)) {
            el.appendChild(document.createTextNode(seg.text));
          } else {
            const span = document.createElement('span');
            span.className = `diff-word-${side}`;
            span.textContent = seg.text;
            el.appendChild(span);
          }
        } else {
          el.appendChild(document.createTextNode(seg.text));
        }
      });
    } else {
      el.className = `diff-line diff-line-${line.type}`;
      el.textContent = line.text || '\u00A0';
    }
    return el;
  }

  /**
   * Setup synchronized scrolling between editor overlay and diff panel
   * Since both have same number of lines, we can sync scrollTop directly
   */
  setupScrollSync() {
    const editorOverlay = this.editorOverlay;
    const modifiedContainer = document.getElementById('diff-modified-lines');

    if (!editorOverlay || !modifiedContainer) return;

    // Remove old listeners if any
    if (this._scrollSyncCleanup) {
      this._scrollSyncCleanup();
    }

    let isSyncing = false;

    const syncScroll = (source, target) => {
      if (isSyncing) return;
      isSyncing = true;
      // Direct scrollTop sync - works because both have same line count
      target.scrollTop = source.scrollTop;
      requestAnimationFrame(() => {
        isSyncing = false;
      });
    };

    const onEditorScroll = () => syncScroll(editorOverlay, modifiedContainer);
    const onPanelScroll = () => syncScroll(modifiedContainer, editorOverlay);

    editorOverlay.addEventListener('scroll', onEditorScroll);
    modifiedContainer.addEventListener('scroll', onPanelScroll);

    // Store cleanup function
    this._scrollSyncCleanup = () => {
      editorOverlay.removeEventListener('scroll', onEditorScroll);
      modifiedContainer.removeEventListener('scroll', onPanelScroll);
    };
  }

  show() {
    if (!this.panel) return;
    if (!this.hasDiff()) return;

    // Erst jetzt ins DOM schreiben — siehe prepareDiff.
    if (!this.rendered) {
      this.renderDiff(this.diff);
      this.rendered = true;
    }

    // Close all open panels before showing diff
    this.closeAllPanels();

    this.isVisible = true;
    this.panel.classList.add('visible');
    const closeFloating = document.getElementById('diff-close-floating');
    if (closeFloating) closeFloating.classList.add('visible');

    // Show editor overlay, hide actual editor
    if (this.editorOverlay) {
      this.editorOverlay.classList.add('visible');
    }

    const editorContent = document.querySelector('.editor-simple-mde-container');
    if (editorContent) {
      editorContent.style.display = 'none';
    }

    // Shrink editor panel for split view
    const editorPanel = document.getElementById('markdown-editor');
    if (editorPanel) {
      editorPanel.classList.add('split-with-diff');

      // Match diff panel width to editor panel width
      requestAnimationFrame(() => this.syncPanelWidth());
    }

    // Re-setup scroll sync when showing
    this.setupScrollSync();

    this.updateToggleButton();
  }

  hide() {
    if (!this.panel) return;

    this.isVisible = false;
    this.panel.classList.remove('visible');
    const closeFloating = document.getElementById('diff-close-floating');
    if (closeFloating) closeFloating.classList.remove('visible');

    // Hide editor overlay, show actual editor
    if (this.editorOverlay) {
      this.editorOverlay.classList.remove('visible');
    }

    const editorContent = document.querySelector('.editor-simple-mde-container');
    if (editorContent) {
      editorContent.style.display = '';
    }

    // Restore editor panel
    const editorPanel = document.getElementById('markdown-editor');
    if (editorPanel) {
      editorPanel.classList.remove('split-with-diff');
    }

    // Cleanup scroll sync
    if (this._scrollSyncCleanup) {
      this._scrollSyncCleanup();
      this._scrollSyncCleanup = null;
    }

    this.updateToggleButton();
  }

  toggle() {
    if (this.isVisible) {
      this.hide();
    } else if (this.hasDiff()) {
      this.show();
    }
  }

  /**
   * Check if there's a diff to show
   */
  hasDiff() {
    return this.diff !== null && (this.diff.stats.added > 0 || this.diff.stats.removed > 0);
  }

  /**
   * Close all open panels before showing diff view
   */
  closeAllPanels() {
    // Neben dem Editor koennen nur noch Chat und Vault offen sein.
    if (window.chatManager) {
      window.chatManager.hideChatPanel();
    }

    if (window.obsidianVault) {
      window.obsidianVault.close();
    }
  }

  updateToggleButton() {
    const btn = document.getElementById('diff-toggle-btn');
    if (!btn) return;

    const has = this.hasDiff();
    btn.classList.toggle('active', this.isVisible);
    btn.classList.toggle('has-diff', has);
    btn.disabled = !has;

    const removedEl = document.getElementById('diff-count-removed');
    const addedEl = document.getElementById('diff-count-added');
    // Eine Pille mit "-0" waere nur Rauschen: leere Zaehler bleiben weg.
    const removed = has ? this.diff.stats.removed : 0;
    const added = has ? this.diff.stats.added : 0;
    if (removedEl) {
      removedEl.textContent = '-' + removed;
      removedEl.hidden = removed === 0;
    }
    if (addedEl) {
      addedEl.textContent = '+' + added;
      addedEl.hidden = added === 0;
    }
  }

  /**
   * Clear the diff view
   */
  clear() {
    this.originalText = '';
    this.modifiedText = '';
    this.diff = null;
    this.rendered = false;

    if (this.editorOverlay) {
      this.editorOverlay.innerHTML = '';
    }

    const modifiedContainer = document.getElementById('diff-modified-lines');
    const statsContainer = document.getElementById('diff-stats');

    if (modifiedContainer) modifiedContainer.innerHTML = '';
    if (statsContainer) statsContainer.innerHTML = '';

    this.hide();
    this.updateToggleButton();
  }
}

// Export singleton instance
export const diffView = new DiffView();
